import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import "./About.css";
import profilePhoto from "./profile.jpg";
import { formatExperience, getExperienceDuration } from "./experience";

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "credentials", label: "Credentials" },
  { id: "off-duty", label: "Off duty" },
];

const focusAreas = [
  {
    code: "01",
    title: "Reliable data platforms",
    copy: "Cloud and hybrid systems built for production, not just the demo.",
  },
  {
    code: "02",
    title: "Quality and governance",
    copy: "Observable pipelines, trustworthy data, and clear ownership from source to consumer.",
  },
  {
    code: "03",
    title: "Technical leadership",
    copy: "Practical architecture, calm delivery, and mentorship that helps the whole team move faster.",
  },
  {
    code: "04",
    title: "Efficient operations",
    copy: "Less toil, better monitoring, and infrastructure choices that respect cost and reliability.",
  },
];

const education = [
  {
    school: "Georgia State University",
    degree: "M.S. Information Systems - Big Data & Analytics",
    year: "2021",
    image: "/images/georgiastate.png",
  },
  {
    school: "Georgia Gwinnett College",
    degree: "B.S. Software Development - Cum Laude",
    year: "2017",
    image: "/images/georgiagwinnett.png",
  },
];

const certifications = [
  {
    name: "AWS Certified Data Engineer - Associate",
    issuer: "Amazon Web Services",
    image: "/images/awsde.png",
    href: "https://www.credly.com/badges/01c5bad0-0f17-4611-8908-235b1b863e65",
  },
  {
    name: "AWS Certified Database - Specialty",
    issuer: "Amazon Web Services",
    image: "/images/awsd.png",
    href: "https://www.credly.com/badges/cca37f0a-4798-427b-a5c7-4de8f9d462e7",
  },
  {
    name: "AWS Certified Solutions Architect - Associate",
    issuer: "Amazon Web Services",
    image: "/images/awssa.png",
    href: "https://www.credly.com/badges/975862c4-d1e8-4b42-8947-b3e7c1053162",
  },
];

const citizenships = [
  { name: "United States", detail: "Citizen", image: "/images/USA.webp" },
  { name: "Serbia", detail: "Citizen", image: "/images/Serbian.webp" },
  { name: "Croatia", detail: "Citizen / EU", image: "/images/croatia.png" },
];

const games = [
  { name: "Metal Gear Solid V", type: "Game", image: "/images/MGS5.png" },
  { name: "Skyrim", type: "Game", image: "/images/Skyrim.jpg" },
  { name: "Civilization V", type: "Game", image: "/images/Civilization V.jpg" },
  { name: "Blasphemous", type: "Game", image: "/images/Blasphemous.webp" },
  { name: "Frostpunk", type: "Game", image: "/images/Frostpunk.gif" },
  { name: "Returnal", type: "Game", image: "/images/Returnal.gif" },
  { name: "S.T.A.L.K.E.R. 2", type: "Game", image: "/images/Chornobyl.jpg" },
  { name: "Kingdom Come: Deliverance", type: "Game", image: "/images/Deliverance.jpg" },
  { name: "Splinter Cell", type: "Game", image: "/images/Tom Clancy's Splinter Cell.jpg" },
  { name: "Total War", type: "Game", image: "/images/Total War Series.jpg" },
  { name: "Warzone", type: "Game", image: "/images/Warzone.jpg" },
  { name: "Wolfenstein", type: "Game", image: "/images/Wolfenstein.gif" },
];

const music = [
  { name: "GIMS", type: "Music", image: "/images/GIMS.jpg" },
  { name: "Grše", type: "Music", image: "/images/Grše.jpg" },
  { name: "KÖFN", type: "Music", image: "/images/KÖFN.jpg" },
  { name: "Lomepal", type: "Music", image: "/images/Lomepal.jpg" },
  { name: "Nek", type: "Music", image: "/images/Nek.jpg" },
  { name: "ODESZA", type: "Music", image: "/images/ODESZA.gif" },
  { name: "Roosevelt", type: "Music", image: "/images/Roosevelt.png" },
  { name: "Senidah", type: "Music", image: "/images/Senidah.jpg" },
  { name: "Simge", type: "Music", image: "/images/Simge.jpg" },
  { name: "Tame Impala", type: "Music", image: "/images/Tame Impala.jpg" },
  { name: "TR/ST", type: "Music", image: "/images/TRST.jpg" },
  { name: "Eagle Eyed Tiger", type: "Music", image: "/images/Eagle Eyed Tiger.jpg" },
];

export default function About() {
  const [activeTab, setActiveTab] = useState("overview");
  const experience = useMemo(() => formatExperience(getExperienceDuration()), []);

  return (
    <>
      <Helmet>
        <title>About | Khaled Asad</title>
        <meta
          name="description"
          content="About Khaled Asad, a senior data engineer working across cloud and hybrid data platforms."
        />
      </Helmet>

      <div className="aboutPage">
        <article className="aboutPanel cp-panel" aria-labelledby="about-title">
          <header className="aboutHero">
            <div className="aboutPortraitFrame">
              <img className="aboutPortrait" src={profilePhoto} alt="Khaled Asad" />
              <span className="aboutPortraitMarker" aria-hidden="true">ATL / ZAD</span>
            </div>

            <div className="aboutHeroCopy">
              <p className="aboutKicker">ABOUT / FIELD NOTES</p>
              <h1 id="about-title">Khaled Asad</h1>
              <p className="aboutRole">Senior Data Engineer at Cargill</p>
            </div>
            <p className="aboutSummary">
              I build reliable cloud and hybrid data platforms, then make them easier to operate,
              understand, and trust. My work sits between architecture, hands-on engineering, and
              helping teams deliver well.
            </p>
          </header>

          <dl className="aboutSignalBar" aria-label="Professional snapshot">
            <div>
              <dt>Experience</dt>
              <dd>{experience}</dd>
            </div>
            <div>
              <dt>Current</dt>
              <dd>Cargill / Data Engineering</dd>
            </div>
            <div>
              <dt>Locations</dt>
              <dd>Atlanta / Zadar</dd>
            </div>
          </dl>

          <div className="aboutTabs" role="tablist" aria-label="About sections">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                id={`about-tab-${tab.id}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`about-panel-${tab.id}`}
                className={activeTab === tab.id ? "is-active" : ""}
                onClick={() => setActiveTab(tab.id)}
              >
                <span aria-hidden="true">0{index + 1}</span>
                {tab.label}
              </button>
            ))}
          </div>

          <section
            id={`about-panel-${activeTab}`}
            className="aboutTabPanel"
            role="tabpanel"
            aria-labelledby={`about-tab-${activeTab}`}
          >
            {activeTab === "overview" && (
              <div className="aboutOverview">
                <div>
                  <p className="aboutSectionLabel">WHAT I FOCUS ON</p>
                  <div className="aboutFocusList">
                    {focusAreas.map((area) => (
                      <div className="aboutFocusRow" key={area.code}>
                        <span>{area.code}</span>
                        <div>
                          <h2>{area.title}</h2>
                          <p>{area.copy}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <aside className="aboutSideNotes" aria-label="Additional details">
                  <div>
                    <p className="aboutSectionLabel">IN PROGRESS</p>
                    <h2>Snowflake Data Engineer certification</h2>
                    <p>Working through the certification path alongside current platform work.</p>
                  </div>
                  <div>
                    <p className="aboutSectionLabel">COMMUNITY</p>
                    <h2>Cargill Cares Council board</h2>
                    <p>Supporting nonprofit partnerships, volunteering, fundraising, and local outreach.</p>
                  </div>
                  <div>
                    <p className="aboutSectionLabel">LANGUAGES</p>
                    <p>English / Arabic / Croatian / Serbian</p>
                  </div>
                  <div className="aboutCitizenship">
                    <p className="aboutSectionLabel">CITIZENSHIP</p>
                    <ul className="aboutCitizenshipFlags" aria-label="USA, Serbian, and Croatian citizenship">
                      {citizenships.map((item) => (
                        <li key={item.name}>
                          <span className="aboutFlagFrame" aria-hidden="true">
                            <img src={item.image} alt="" />
                          </span>
                          <span>
                            <strong>{item.name}</strong>
                            <small>{item.detail}</small>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <span className="aboutMobilityNote">North American and EU mobility</span>
                  </div>
                </aside>
              </div>
            )}

            {activeTab === "credentials" && (
              <div className="aboutCredentials">
                <section>
                  <p className="aboutSectionLabel">EDUCATION</p>
                  <div className="credentialRows">
                    {education.map((item) => (
                      <div className="credentialRow" key={item.school}>
                        <img src={item.image} alt="" />
                        <div>
                          <h2>{item.school}</h2>
                          <p>{item.degree}</p>
                        </div>
                        <time>{item.year}</time>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <p className="aboutSectionLabel">CERTIFICATIONS</p>
                  <div className="credentialRows">
                    {certifications.map((item) => (
                      <a
                        className="credentialRow credentialRowLink"
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={item.name}
                      >
                        <img src={item.image} alt="" />
                        <div>
                          <h2>{item.name}</h2>
                          <p>{item.issuer}</p>
                        </div>
                        <span aria-hidden="true">View</span>
                      </a>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === "off-duty" && (
              <div className="aboutOffDuty">
                <div className="aboutOffDutyIntro">
                  <p className="aboutSectionLabel">AFTER HOURS</p>
                  <h2>The full after-hours signal.</h2>
                  <p>
                    Strategy, strange worlds, dark electronic music, and a persistent soft spot for
                    Croatia. The things I keep coming back to outside work.
                  </p>
                </div>
                <div className="aboutCollections">
                  <section className="aboutCollection" aria-labelledby="games-heading">
                    <header className="aboutCollectionHeader">
                      <h2 id="games-heading">Games</h2>
                      <span>{games.length} titles</span>
                    </header>
                    <div className="aboutMediaGrid">
                      {games.map((item) => (
                        <figure key={item.name}>
                          <img src={item.image} alt="" loading="lazy" />
                          <figcaption>
                            <span>{item.type}</span>
                            <strong>{item.name}</strong>
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </section>

                  <section className="aboutCollection" aria-labelledby="music-heading">
                    <header className="aboutCollectionHeader">
                      <h2 id="music-heading">Music</h2>
                      <span>{music.length} artists</span>
                    </header>
                    <div className="aboutMediaGrid">
                      {music.map((item) => (
                        <figure key={item.name}>
                          <img src={item.image} alt="" loading="lazy" />
                          <figcaption>
                            <span>{item.type}</span>
                            <strong>{item.name}</strong>
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </section>
                </div>
              </div>
            )}
          </section>
        </article>
      </div>
    </>
  );
}
